import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { AccountDetail } from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import { AccountQueryKeys } from './accountQueryKeys';

export type UseAccountDetailResult = {
    /** The account with every locale of its name; `null` until loaded. */
    data: AccountDetail | null;
    isLoading: boolean;
    error: ErrorResponse | null;
    refetch: () => void;
    /**
     * Re-read and **resolve the fresh record**, rather than only nudging the
     * cache. This is what stale-update recovery needs: on a `409` the form has
     * to compare the server's current state against what the user was editing
     * *before* it can decide whether to replay or ask. Resolves `null` when the
     * re-read itself failed.
     */
    refetchDetail: () => Promise<AccountDetail | null>;
};

/**
 * One account, with the **full locale map** for its name.
 *
 * The edit drawer has separate Georgian and English name fields, and the list
 * only carries one server-resolved string — so opening the drawer has to fetch
 * the account. Without this, editing an account in Georgian would silently blank
 * its English name on save.
 *
 * It is also where the drawer gets the account's **current etag**. Re-reading on
 * open means an edit is made against the version the user is actually looking
 * at, which is what makes a `409 STALE_UPDATE` a rare surprise rather than the
 * normal case.
 *
 * `staleTime: 0` on purpose: this is the concurrency token's source of truth, and
 * a cached one is a stale one.
 */
export const useAccountDetailQuery = (
    id: string | null,
): UseAccountDetailResult => {
    const { apiClient } = useApi();

    const query = useQuery({
        queryKey: AccountQueryKeys.detail(id ?? ''),
        queryFn: () => apiClient.accounts.getAccount(id as string),
        enabled: id !== null,
        staleTime: 0,
    });

    const response = query.data;

    return {
        data: response?.success ? response.response : null,
        isLoading: id !== null && query.isLoading,
        error: response && !response.success ? response.error : null,
        refetch: () => {
            query.refetch();
        },
        refetchDetail: async () => {
            const next = await query.refetch();
            const envelope = next.data;
            return envelope?.success ? envelope.response : null;
        },
    };
};
